import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { calculateSponsorAmounts, getParticipantBreakdown } from '../../utils/helpers';
import { Expense, Settlement, SponsorExpense } from '../../types';

interface ReceiverSummaryProps {
    participantId: string;
    participantName: string;
    participantIds: string[];
    expenses: (Expense | SponsorExpense)[];
    // The plan's own transfers, so the totals here are the sums of the cards below.
    settlements: Settlement[];
}

/**
 * The four numbers someone who handles money in the settlement needs to check
 * it: what comes in, what goes out, what they spent, and what their share was.
 *
 * Each transfer card explains its amount from the sender's side, so a person
 * money is sent to only ever saw a list of amounts. A net "you get back" figure
 * did not help either: a collector takes everyone's money and passes some of it
 * on, and needs both halves to recognise the transfers they actually make. The
 * check under the tiles is the sum they would otherwise do in their head.
 */
const ReceiverSummary: React.FC<ReceiverSummaryProps> = ({
                                                             participantId,
                                                             participantName,
                                                             participantIds,
                                                             expenses,
                                                             settlements
                                                         }) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();

    const summary = useMemo(() => {
        let incoming = 0;
        let outgoing = 0;
        let senders = 0;
        let recipients = 0;
        settlements.forEach(settlement => {
            if (settlement.to === participantId) {
                incoming += settlement.amount;
                senders++;
            }
            if (settlement.from === participantId) {
                outgoing += settlement.amount;
                recipients++;
            }
        });

        // Anyone money is sent to, including a collector who ends up owing:
        // someone who only pays already has their card's breakdown.
        if (incoming <= 0) return null;

        const paid = Math.round(getParticipantBreakdown(expenses, participantId).totalPaid);
        // Sponsor cash in their hands was netted into the transfers, so it has to
        // come off here too or a holder's figures would not add up.
        const held = Math.round(calculateSponsorAmounts(expenses, participantIds)[participantId] || 0);
        const net = incoming - outgoing;
        // Derived from the transfers rather than the balance: the transfers are
        // rounded, and the check under the tiles has to come out to the đồng.
        const share = paid - net - held;

        return { incoming, outgoing, senders, recipients, paid, share, held, net };
    }, [settlements, expenses, participantId, participantIds]);

    if (!summary) return null;

    const { incoming, outgoing, senders, recipients, paid, share, held, net } = summary;

    // A sponsorship surplus handed out to members can push the share below zero,
    // and a collector who owes overall sends out more than comes in.
    const signedCurrency = (value: number) => (
        `${value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`
    );

    const tiles = [
        {
            key: 'incoming',
            label: t('event.payments.receiverSummary.incoming'),
            hint: t('event.payments.receiverSummary.incomingHint', { count: senders }),
            value: `+${formatCurrency(incoming)}`,
            className: 'text-green-600 dark:text-green-400'
        },
        {
            key: 'outgoing',
            label: t('event.payments.receiverSummary.outgoing'),
            hint: recipients > 0
                ? t('event.payments.receiverSummary.outgoingHint', { count: recipients })
                : t('event.payments.receiverSummary.outgoingNone'),
            value: outgoing > 0 ? `−${formatCurrency(outgoing)}` : formatCurrency(0),
            className: outgoing > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
        },
        {
            key: 'paid',
            label: t('event.payments.receiverSummary.paid'),
            hint: t('event.payments.receiverSummary.paidHint'),
            value: formatCurrency(paid),
            className: 'text-gray-900 dark:text-white'
        },
        {
            key: 'share',
            label: t('event.payments.receiverSummary.share'),
            hint: t('event.payments.receiverSummary.shareHint'),
            value: signedCurrency(share),
            className: 'text-gray-900 dark:text-white'
        }
    ];

    return (
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 truncate">
                {t('event.payments.receiverSummary.title')}
                <span className="normal-case font-normal tracking-normal">{` · ${participantName}`}</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {tiles.map(tile => (
                    <div key={tile.key} className="glass-sub-panel px-3 py-2.5">
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 mb-0.5">{tile.label}</div>
                        <div className={`text-base font-bold tabular-nums ${tile.className}`}>{tile.value}</div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{tile.hint}</div>
                    </div>
                ))}
            </div>

            <p className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-300 mt-2">
                <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0 text-green-600 dark:text-green-400" aria-hidden="true"/>
                <span>
                    {held > 0
                        ? t('event.payments.receiverSummary.checkHeld', {
                            net: signedCurrency(net),
                            held: formatCurrency(held)
                        })
                        : t('event.payments.receiverSummary.check', { net: signedCurrency(net) })}
                </span>
            </p>
        </div>
    );
};

export default ReceiverSummary;
